import { RouterProvider, createRouter } from '@tanstack/react-router';
import React from 'react';
import ReactDOM from 'react-dom/client';

import { createQueryClient } from './queryClient';
import { routeTree } from './routeTree.gen';
import { applyBrandingToDocument, loadBranding } from './shared/branding';
import { applyLocaleDirection, initI18n } from './shared/i18n';

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof buildRouter>;
  }
}

function buildRouter() {
  const queryClient = createQueryClient();
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
  });
}

async function bootstrap(): Promise<void> {
  // Branding loads before first render so the client's name/logo never flash
  // in after a placeholder; it falls back to a neutral identity on failure.
  const [i18n, branding] = await Promise.all([initI18n(), loadBranding()]);
  applyBrandingToDocument(branding);
  applyLocaleDirection(i18n.language);
  i18n.on('languageChanged', applyLocaleDirection);

  const router = buildRouter();
  const rootElement = document.getElementById('root');
  if (!rootElement) throw new Error('Root element "#root" not found in index.html');

  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <RouterProvider router={router} />
    </React.StrictMode>,
  );
}

bootstrap().catch((error: unknown) => {
  console.error('Failed to bootstrap React application:', error);
});
